import React, { useEffect, useState } from 'react';
import { Activity, RefreshCw, History } from 'lucide-react';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import { BACKEND_URL } from '../lib/api';
import { relTime } from '../lib/compliance';
import { cn } from '../lib/utils';

const LABELS = { mongo: 'Database', smtp: 'Email (SMTP)', storage: 'Storage', scheduler: 'Scheduler', ai: 'AI extraction', messaging: 'Messaging' };

function tone(key, c) {
  if (!c) return 'grey';
  if (!c.ok) return 'red';
  if ((key === 'smtp' && !c.configured) || (key === 'ai' && (!c.provider || c.provider === 'none')) || (key === 'scheduler' && !c.enabled) || (key === 'messaging' && !c.configured)) return 'amber';
  return 'green';
}
const DOT = { green: 'bg-emerald-500', amber: 'bg-amber-400', red: 'bg-rose-500', grey: 'bg-ink-300' };
function detail(key, c) {
  if (!c) return '—';
  if (!c.ok) return c.error || c.lastError || 'failing';
  if (key === 'mongo') return 'connected';
  if (key === 'smtp') return c.configured ? c.host : 'not configured';
  if (key === 'storage') return c.provider === 'local' ? 'local disk' : `${c.provider}${c.bucket ? ` · ${c.bucket}` : ''}`;
  if (key === 'scheduler') return !c.enabled ? 'disabled' : c.running ? 'running now' : c.lastRunAt ? `last run ${relTime(c.lastRunAt)}` : 'not run yet';
  if (key === 'ai') return c.provider && c.provider !== 'none' ? c.provider : 'no provider';
  if (key === 'messaging') { const on = Object.entries(c.channels || {}).filter(([, v]) => v).map(([k]) => ({ whatsapp: 'WhatsApp', sms: 'SMS', discord: 'Discord', webhook: 'Webhook' }[k] || k)); return on.length ? on.join(' · ') : 'email only'; }
  return '';
}

const BUCKETS = 84; // 7 days × 12 two-hour buckets

function HealthHistory() {
  const app = useApp();
  const [hist, setHist] = useState(null);
  useEffect(() => { app.getHealthHistory(7).then(setHist); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!hist) return <div className="text-xs text-ink-400 px-1 py-2">Loading history…</div>;
  const end = Date.now(); const span = 7 * 864e5; const start = end - span;
  const cells = Object.fromEntries(Object.keys(LABELS).map((k) => [k, Array.from({ length: BUCKETS }, () => null)]));
  hist.samples.forEach((s) => { const i = Math.min(BUCKETS - 1, Math.max(0, Math.floor(((new Date(s.at).getTime() - start) / span) * BUCKETS))); Object.keys(LABELS).forEach((k) => { const ok = s.checks?.[k]; if (ok === undefined) return; cells[k][i] = cells[k][i] === false ? false : ok; }); });
  const flaps = (row) => row.filter((v, i) => i && v !== null && row[i - 1] !== null && v !== row[i - 1]).length;
  return (
    <div data-testid="health-history" className="w-full pt-2 border-t border-line mt-1">
      <div className="flex items-center justify-between text-[10px] text-ink-400 mb-1"><span>7 days ago</span><span>{hist.samples.length} samples · one cell = 2h · red = any failure in that window</span><span>now</span></div>
      {Object.keys(LABELS).map((k) => (
        <div key={k} className="flex items-center gap-2 py-0.5" data-testid={`health-history-${k}`}>
          <span className="w-24 text-[11px] text-ink-600 shrink-0">{LABELS[k]}</span>
          <div className="flex-1 flex gap-px h-3">{cells[k].map((v, i) => <span key={i} className={cn('flex-1 rounded-[1px]', v === null ? 'bg-paper-200' : v ? 'bg-emerald-400' : 'bg-rose-500')} />)}</div>
          <span className={cn('w-16 text-right text-[10px]', flaps(cells[k]) > 2 ? 'text-rose-600 font-medium' : 'text-ink-400')} title="State changes in the last 7 days">{flaps(cells[k]) ? `${flaps(cells[k])} flip${flaps(cells[k]) === 1 ? '' : 's'}` : 'steady'}</span>
        </div>
      ))}
    </div>
  );
}

export function HealthStrip({ compact = false }) {
  const [showHist, setShowHist] = useState(false);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(false);
  const load = async () => { try { const { data } = await axios.get(`${BACKEND_URL}/api/health`, { validateStatus: () => true }); setHealth(data); setError(false); } catch { setError(true); } };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const checks = health?.checks || {};
  const overall = error ? 'red' : !health ? 'grey' : health.status === 'ok' ? 'green' : 'red';
  return (
    <div data-testid="health-strip" data-compact={compact || undefined} className="rounded-xl border border-line bg-white shadow-card px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink-800"><Activity className="h-4 w-4 text-ink-400" />System health<span data-testid="health-overall" className={cn('h-2.5 w-2.5 rounded-full', DOT[overall])} title={error ? 'API unreachable' : health?.status || 'loading'} /></div>
      {Object.keys(LABELS).map((k) => { const t = tone(k, checks[k]); return (
        <div key={k} data-testid={`health-${k}`} data-tone={t} className="flex items-center gap-2 text-xs">
          <span className={cn('h-2 w-2 rounded-full shrink-0', DOT[t])} />
          <span className="font-medium text-ink-700">{LABELS[k]}</span>
          <span className="text-ink-400 truncate max-w-[180px]" title={detail(k, checks[k])}>{detail(k, checks[k])}</span>
        </div>
      ); })}
      <div className="ml-auto flex items-center gap-2">
        {!compact && <button data-testid="health-history-toggle" onClick={() => setShowHist(!showHist)} className={cn('inline-flex items-center gap-1 text-xs transition-colors', showHist ? 'text-ink-800' : 'text-ink-400 hover:text-ink-700')} title="7-day history"><History className="h-3.5 w-3.5" />History</button>}
        <button data-testid="health-refresh" onClick={load} className="text-ink-400 hover:text-ink-700 transition-colors" title="Refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
      </div>
      {showHist && !compact && <HealthHistory />}
    </div>
  );
}
